import { can } from '@clim/client-core';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { useReady, useSession } from './lib/session';
import { AdminLayout } from './pages/admin/Admin';
import { AuditAdmin } from './pages/admin/Audit';
import { ReferentialsAdmin } from './pages/admin/Referentials';
import { RolesAdmin } from './pages/admin/Roles';
import { TerritoriesAdmin } from './pages/admin/Territories';
import { UsersAdmin } from './pages/admin/Users';
import { ChangePasswordPage } from './pages/ChangePassword';
import { ClientDetailPage, ClientsPage } from './pages/Clients';
import { DossierDetailPage } from './pages/DossierDetail';
import { DossiersPage } from './pages/Dossiers';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage } from './pages/Login';
import { SecurityPage } from './pages/Security';
import { MessagingAdmin } from './pages/admin/Messaging';
import { NewDossierPage } from './pages/NewDossier';
import { HomePage } from './pages/Home';
import { SettingsAdmin } from './pages/admin/Settings';
import { CatalogAdmin } from './pages/admin/Catalog';
import { LegalTextsAdmin } from './pages/admin/LegalTexts';
import { SyncIssuesPage } from './pages/SyncIssues';
import { PortalPage } from './pages/Portal';
import { NotificationsPage } from './pages/Notifications';
import { PlanningPage } from './pages/Planning';
import { MyInterventionsPage } from './pages/MyInterventions';
import { RemittancesPage } from './pages/Remittances';
import { CommissionsPage } from './pages/Commissions';
import { CommissionsTeamPage } from './pages/CommissionsTeam';
import { SavListPage } from './pages/SavList';
import { QualityPage } from './pages/Quality';
import { StockPage } from './pages/Stock';
import { ContractorDetailPage, ContractorsPage, MyDocumentsPage } from './pages/Contractors';
import { PaymentReturnPage } from './pages/PaymentReturn';
import { VerifyPickupPage } from './pages/VerifyPickup';
import { PaymentMethodsAdmin } from './pages/admin/PaymentMethods';
import { TeamsAdmin } from './pages/admin/Teams';
import { NotificationSettingsAdmin } from './pages/admin/NotificationSettings';
import { AnnouncementsPage } from './pages/Announcements';
import { ProspectsPage } from './pages/Prospects';
import { DashboardPage } from './pages/Dashboard';
import { ReviewsPage } from './pages/Reviews';
import { InvoicingPage } from './pages/Invoicing';
import { PlatformPage } from './pages/Platform';

function AdminIndex() {
  const { me } = useReady();
  const first = can(me, 'catalog.manage')
    ? 'catalogue'
    : can(me, 'user.read')
      ? 'utilisateurs'
      : can(me, 'role.read')
        ? 'roles'
        : can(me, 'territory.manage')
          ? 'territoires'
          : can(me, 'referential.manage')
            ? 'referentiels'
            : can(me, 'payment_method.manage')
              ? 'paiements'
              : 'audit';
  return <Navigate to={first} replace />;
}

export function App() {
  const { state, session } = useSession();
  const { pathname } = useLocation();
  // portail client : public, sans compte, indépendant de toute session
  if (window.location.pathname.startsWith('/portail')) return <PortalPage />;
  // espace plateforme (super-admin) : connexion distincte des entreprises
  if (window.location.pathname.startsWith('/plateforme')) return <PlatformPage />;
  // retour de paiement Stripe : public
  if (window.location.pathname.startsWith('/paiement')) return <PaymentReturnPage />;
  // vérification d'un bon d'enlèvement par le fournisseur : public
  if (window.location.pathname.startsWith('/verifier-bon')) return <VerifyPickupPage />;
  // récupération de compte : accessible sans session
  if (pathname.startsWith('/mot-de-passe-oublie')) return <ForgotPasswordPage />;
  if (pathname.startsWith('/reinitialisation')) return <ResetPasswordPage />;
  if (state === 'loading') return <div className="splash">Chargement…</div>;
  if (!session) return <LoginPage />;
  if (session.me.user.mustChangePassword && !session.offlineStart) return <ChangePasswordPage />;
  if (session.me.user.mfaSetupRequired && !session.offlineStart) return <SecurityPage forced />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="dossiers" element={<DossiersPage />} />
        <Route path="dossiers/nouveau" element={<NewDossierPage />} />
        <Route path="dossiers/:id" element={<DossierDetailPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="sync" element={<SyncIssuesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="planning" element={<PlanningPage />} />
        <Route path="interventions" element={<MyInterventionsPage />} />
        <Route path="encaissements" element={<RemittancesPage />} />
        <Route path="commissions" element={<CommissionsPage />} />
        <Route path="commissions/equipe" element={<CommissionsTeamPage />} />
        <Route path="sav" element={<SavListPage />} />
        <Route path="qualite" element={<QualityPage />} />
        <Route path="stock" element={<StockPage />} />
        <Route path="annonces" element={<AnnouncementsPage />} />
        <Route path="prospects" element={<ProspectsPage />} />
        <Route path="tableau-de-bord" element={<DashboardPage />} />
        <Route path="avis" element={<ReviewsPage />} />
        <Route path="facturation" element={<InvoicingPage />} />
        <Route path="ressources" element={<AnnouncementsPage />} />
        <Route path="mes-documents" element={<MyDocumentsPage />} />
        <Route path="securite" element={<SecurityPage />} />
        <Route path="sous-traitants" element={<ContractorsPage />} />
        <Route path="sous-traitants/:userId" element={<ContractorDetailPage />} />
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminIndex />} />
          <Route path="utilisateurs" element={<UsersAdmin />} />
          <Route path="roles" element={<RolesAdmin />} />
          <Route path="territoires" element={<TerritoriesAdmin />} />
          <Route path="referentiels" element={<ReferentialsAdmin />} />
          <Route path="audit" element={<AuditAdmin />} />
          <Route path="parametres" element={<SettingsAdmin />} />
          <Route path="catalogue" element={<CatalogAdmin />} />
          <Route path="textes" element={<LegalTextsAdmin />} />
          <Route path="paiements" element={<PaymentMethodsAdmin />} />
          <Route path="equipes" element={<TeamsAdmin />} />
          <Route path="notifications" element={<NotificationSettingsAdmin />} />
          <Route path="envois" element={<MessagingAdmin />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
